import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import type { Pool, CreatePoolRequest, UpdatePoolRequest } from '@finvari/deliver';
import { createPool, updatePool, deletePool } from '../firebase';

interface PoolFormDialogProps {
  open: boolean;
  onClose: () => void;
  // Omit to create a new pool; pass the document to edit an existing one.
  pool?: Pool;
  onSaved?: () => void;
}

// Lowercase slug from a free-text id (mirrors the backend sanitizeSlug).
const slugify = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

export default function PoolFormDialog({ open, onClose, pool, onSaved }: PoolFormDialogProps) {
  const isEdit = !!pool;

  const [poolId, setPoolId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultLeaseHours, setDefaultLeaseHours] = useState('4');
  const [canResetData, setCanResetData] = useState(true);
  const [canLoadCustomerData, setCanLoadCustomerData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPoolId(pool?.poolId ?? '');
    setName(pool?.name ?? '');
    setDescription(pool?.description ?? '');
    setDefaultLeaseHours(String(pool?.defaultLeaseHours ?? 4));
    setCanResetData(pool?.canResetData !== false); // default true
    setCanLoadCustomerData(pool?.canLoadCustomerData === true); // default false
    setError(null);
    setConfirmDelete(false);
  }, [open, pool]);

  const leaseHoursNum = Number(defaultLeaseHours);
  const leaseHoursValid = Number.isFinite(leaseHoursNum) && leaseHoursNum > 0;
  const effectiveId = isEdit ? pool!.poolId : slugify(poolId);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('A pool name is required');
      return;
    }
    if (!leaseHoursValid) {
      setError('Default lease hours must be a positive number');
      return;
    }
    if (!isEdit && !effectiveId) {
      setError('A pool id is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        const payload: UpdatePoolRequest = {
          poolId: pool!.poolId,
          name: name.trim(),
          description: description.trim(),
          defaultLeaseHours: leaseHoursNum,
          canResetData,
          canLoadCustomerData,
        };
        await updatePool(payload);
      } else {
        const payload: CreatePoolRequest = {
          poolId: effectiveId,
          name: name.trim(),
          description: description.trim() || undefined,
          defaultLeaseHours: leaseHoursNum,
          canResetData,
          canLoadCustomerData,
        };
        await createPool(payload);
      }
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || `Failed to ${isEdit ? 'update' : 'create'} pool`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setSubmitting(true);
    setError(null);
    try {
      await deletePool(pool!.poolId);
      onSaved?.();
      onClose();
    } catch (e: any) {
      // The backend refuses deletion while the pool has members or queued deploys; surface its message.
      setConfirmDelete(false);
      setError(e?.message || 'Failed to delete pool');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit Pool — ${pool!.name}` : 'New Pool'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Pool ID"
            value={isEdit ? pool!.poolId : poolId}
            onChange={(e) => setPoolId(e.target.value)}
            fullWidth
            required={!isEdit}
            disabled={isEdit || submitting}
            helperText={
              isEdit
                ? undefined
                : effectiveId
                ? `Will be saved as "${effectiveId}"`
                : 'A short slug used as the pool identifier'
            }
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={submitting}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={submitting}
          />
          <TextField
            label="Default lease hours"
            value={defaultLeaseHours}
            onChange={(e) => setDefaultLeaseHours(e.target.value)}
            type="number"
            fullWidth
            required
            disabled={submitting}
            error={!!defaultLeaseHours && !leaseHoursValid}
            helperText="Applied to new leases acquired from this pool"
          />
          {/* Guardrails are defined on the pool and forced onto every member environment. Editing
              them here rewrites all current members to match. */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={canResetData}
                  onChange={(e) => setCanResetData(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Allow database to be wiped"
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={canLoadCustomerData}
                  onChange={(e) => setCanLoadCustomerData(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Has access to customer snapshots (requires manual IAM grant)"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            These guardrails apply to every environment in the pool. Member environments inherit them
            and cannot override them individually.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isEdit && (
          <>
            <Button color="error" onClick={() => setConfirmDelete(true)} disabled={submitting}>
              Delete
            </Button>
            <Box sx={{ flex: 1 }} />
          </>
        )}
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={submitting || !name.trim() || !leaseHoursValid || (!isEdit && !effectiveId)}
          startIcon={submitting ? <CircularProgress size={20} /> : undefined}
        >
          {submitting ? 'Saving...' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>

      <Dialog open={confirmDelete} onClose={submitting ? undefined : () => setConfirmDelete(false)}>
        <DialogTitle>Delete pool?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{pool?.name}" will be removed. This is only allowed once the pool has no member
            environments and no queued deploys.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            Delete pool
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
